import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Heart, X, ChevronDown } from 'lucide-react';
import { useWishlistItems, useQuickAddWishlist, useDeleteWishlistItem } from '@/hooks/useApi';
import { useAuthStore } from '@/store';

interface Props {
  cardName: string;
  cardImageUrl?: string;
  setName?: string;
  className?: string;
}

interface PopoverPos {
  top: number;
  left: number;
}

const POPOVER_W = 256; // w-64
const POPOVER_H = 260; // approximate max height
const MARGIN    = 8;   // gap from viewport edge

const PRICE_MODES = [
  { value: 'none', label: 'Any price' },
  { value: 'max',  label: 'Up to…'   },
];

export default function WishlistButton({ cardName, cardImageUrl, setName, className = '' }: Props) {
  const { isAuthenticated } = useAuthStore();
  const { data: wishlistItems = [] } = useWishlistItems();
  const { mutate: quickAdd, isLoading: adding } = useQuickAddWishlist();
  const { mutate: removeItem, isLoading: removing } = useDeleteWishlistItem();

  const [open, setOpen]           = useState(false);
  const [pos, setPos]             = useState<PopoverPos>({ top: 0, left: 0 });
  const [priceMode, setPriceMode] = useState<'none' | 'max'>('none');
  const [priceMax, setPriceMax]   = useState('');
  const buttonRef                 = useRef<HTMLButtonElement>(null);
  const popoverRef                = useRef<HTMLDivElement>(null);

  const existing = wishlistItems.find(
    (w: any) => w.card_name.toLowerCase() === cardName.toLowerCase() && w.is_active
  );
  const isWishlisted = !!existing;

  // Calculate popover position so it stays fully on screen
  const calcPos = useCallback(() => {
    if (!buttonRef.current) return;
    const r   = buttonRef.current.getBoundingClientRect();
    const vw  = window.innerWidth;
    const vh  = window.innerHeight;

    // Horizontal: prefer opening to the left of the button; if too close to left edge open right
    let left = r.right - POPOVER_W;
    if (left < MARGIN) left = r.left; // open right instead
    if (left + POPOVER_W > vw - MARGIN) left = vw - MARGIN - POPOVER_W; // clamp right edge

    // Vertical: prefer opening upward above the button; if too close to top open downward
    let top = r.top - POPOVER_H - MARGIN;
    if (top < MARGIN) top = r.bottom + MARGIN; // open downward instead
    if (top + POPOVER_H > vh - MARGIN) top = vh - MARGIN - POPOVER_H; // clamp bottom edge

    setPos({ top, left });
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current  && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Recalculate on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => calcPos();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, calcPos]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) return;
    if (isWishlisted) {
      removeItem(existing!.id);
      return;
    }
    setPriceMode('none');
    setPriceMax('');
    calcPos();
    setOpen(true);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    quickAdd({
      card_name:      cardName,
      card_image_url: cardImageUrl,
      set_name:       setName,
      price_mode:     priceMode,
      price_max:      priceMode === 'max' && priceMax ? parseFloat(priceMax) : undefined,
    });
    setOpen(false);
  };

  if (!isAuthenticated) return null;

  return (
    <div className={`w-8 h-8 ${className}`} onClick={(e) => e.stopPropagation()}>
      {/* Heart button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleButtonClick}
        disabled={adding || removing}
        title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        className={`flex items-center justify-center w-8 h-8 rounded-full shadow-md transition-all disabled:opacity-50
          ${isWishlisted
            ? 'bg-pink-500 text-white hover:bg-pink-600'
            : 'bg-white dark:bg-zinc-900/90 text-gray-500 dark:text-zinc-500 hover:text-pink-500 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900'
          }`}
      >
        <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-white' : ''}`} />
      </button>

      {/* Popover — rendered in a portal so it's never clipped by overflow:hidden parents */}
      {open && createPortal(
        <div
          ref={popoverRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: POPOVER_W, zIndex: 9999 }}
          className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-gray-200 dark:border-zinc-800 p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="min-w-0 pr-2">
              <p className="font-semibold text-sm text-gray-900 dark:text-zinc-100 truncate">{cardName}</p>
              {setName && <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{setName}</p>}
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="text-gray-400 dark:text-zinc-500 hover:text-gray-500 dark:text-zinc-500 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400 mb-1">Price alert</label>
              <div className="relative">
                <select
                  value={priceMode}
                  onChange={(e) => setPriceMode(e.target.value as 'none' | 'max')}
                  className="w-full text-sm border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 pr-8 appearance-none focus:outline-none focus:ring-2 focus:ring-brand-400"
                >
                  {PRICE_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-500 dark:text-zinc-500 pointer-events-none" />
              </div>
            </div>

            {priceMode === 'max' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400 mb-1">Max price ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  placeholder="e.g. 25.00"
                  className="w-full text-sm border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  required
                />
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-zinc-500 leading-snug">
              You'll be notified when this card is listed, auctioned, or available for trade.
            </p>

            <button
              type="submit"
              disabled={adding}
              className="w-full btn-primary py-2 text-sm disabled:opacity-50"
            >
              {adding ? 'Saving…' : 'Watch this card'}
            </button>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}
